import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { supabaseLeaseService, EnhancedSublease } from '../../../../services/supabase/leases';
import { useClickOutside } from '../../../../hooks/useClickOutside';
import type { RentalContract, RentalContractFormData } from '../../../../types/rental-contract';

// Helper function to format dropdown label for user-friendly display
// Format: "Lease #467 - Anindilyakw... / 2024"
const formatDropdownLabel = (sublease: EnhancedSublease): string => {
  const leaseId = `Lease #${sublease.lease_id}`;
  
  // Truncate holder name to make dropdown readable
  const holderName = sublease.leaseholder_name 
    ? (sublease.leaseholder_name.length > 15 
        ? sublease.leaseholder_name.substring(0, 15) + '...' 
        : sublease.leaseholder_name)
    : 'Unknown';
  
  // Get year from lease_start
  const year = sublease.lease_start 
    ? new Date(sublease.lease_start).getFullYear().toString()
    : 'N/A';
  
  return `${leaseId} - ${holderName} / ${year}`;
};

interface RentalContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: RentalContractFormData) => void;
  title: string;
  initialData?: Partial<RentalContractFormData>;
  preventCloseOnOutsideClick?: boolean;
}

export default function RentalContractModal({
  isOpen,
  onClose,
  onSave,
  title,
  initialData,
  preventCloseOnOutsideClick = true
}: RentalContractModalProps) {
  const modalContentRef = useRef<HTMLDivElement>(null);
  useClickOutside(modalContentRef, onClose, preventCloseOnOutsideClick);

  const [formData, setFormData] = useState<RentalContractFormData>({
    rental_lease: initialData?.rental_lease || null,
    rental_amount: initialData?.rental_amount || null,
    rental_start: initialData?.rental_start || null
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  
  // Fetch subleases for dropdown using getAllEnhanced with explicit FK reference
  // This now uses the fixed query that properly joins to leaseholders
  const { data: subleases = [], isLoading: isLoadingSubleases, error: subleasesError } = useQuery({
    queryKey: ['subleases-for-dropdown-enhanced-v2'],
    queryFn: async () => {
      console.log('Fetching subleases for dropdown using getAllEnhanced...');
      const result = await supabaseLeaseService.getAllEnhanced();
      console.log(`Fetched ${result.length} subleases for dropdown:`, result.slice(0, 3));
      return result;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false
  });

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const newErrors: Record<string, string> = {};
    
    if (!formData.rental_lease) {
      newErrors.rental_lease = 'Lease is required';
    }
    
    if (formData.rental_amount === null) {
      newErrors.rental_amount = 'Amount is required';
    } else if (isNaN(Number(formData.rental_amount)) || Number(formData.rental_amount) < 0) {
      newErrors.rental_amount = 'Amount must be a positive number';
    }
    
    setErrors(newErrors);
    
    // If no errors, save the data
    if (Object.keys(newErrors).length === 0) {
      onSave(formData);
    }
  };
  
  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: name === 'rental_amount' 
        ? value === '' ? null : parseFloat(value)
        : name === 'rental_lease'
          // Convert the selected lease_id string to a number for the rental_lease field
          ? value === '' ? null : parseInt(value, 10)
          : value
    }));
  };
  
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start md:items-center justify-center p-0 z-[100] overflow-y-auto"
    >
      <div 
        ref={modalContentRef}
        className="relative bg-white w-full md:max-w-2xl md:rounded-lg md:my-8 min-h-screen md:min-h-0 max-h-full md:max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex justify-between items-center p-4 md:p-6 border-b bg-white">
          <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
          <button 
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        {/* Content - with scrollable area */}
        <form id="rental-contract-form" onSubmit={handleSubmit} className="p-4 md:p-6 space-y-6 overflow-y-auto flex-1">
          {/* Basic Information */}
          <div className="border-b pb-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Rental Contract Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Lease *
                </label>
                <div className="relative">
                  <select
                    id="rental_lease"
                    name="rental_lease"
                    value={formData.rental_lease || ''}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                    disabled={isLoadingSubleases}
                    required
                  >
                  <option value="">Select a lease</option>
                  {isLoadingSubleases ? (
                    <option value="" disabled>Loading leases...</option>
                  ) : subleasesError ? (
                    <option value="" disabled>Error loading leases</option>
                  ) : subleases.length === 0 ? (
                    <option value="" disabled>No leases available</option>
                  ) : (
                    // Display user-friendly label: "Lease #467 - Holder Name / Year"
                    subleases.map((sublease: EnhancedSublease) => (
                      <option key={sublease.lease_id} value={sublease.lease_id}>
                        {formatDropdownLabel(sublease)}
                      </option>
                    ))
                  )}
                  </select>
                  {isLoadingSubleases && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                    </div>
                  )}
                </div>
                {errors.rental_lease && <div className="text-sm text-red-500 mt-1">{errors.rental_lease}</div>}
                {subleasesError && <div className="text-sm text-red-500 mt-1">Error loading leases. Please try again.</div>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Amount *
                </label>
                <input
                  type="number"
                  id="rental_amount"
                  name="rental_amount"
                  value={formData.rental_amount === null ? '' : formData.rental_amount}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  step="0.01"
                  min="0"
                  required
                />
                {errors.rental_amount && <div className="text-sm text-red-500 mt-1">{errors.rental_amount}</div>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Start Date
                </label>
                <input
                  type="date"
                  id="rental_start"
                  name="rental_start"
                  value={formData.rental_start || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                />
              </div>
            </div>
          </div>
        </form>
        
        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex justify-end gap-3 p-4 md:p-6 border-t bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="rental-contract-form"
            className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-md"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
